/**
 * THE SHARE SHEET — the card, already built, one tap from the thread.
 *
 * The memo's most important UX note, and the reason this is a component and not
 * a button:
 *
 *   "The card composes itself on screen during the final-results bridge, already
 *    built, one tap to send. Any friction between the laugh and the send kills
 *    the send."
 *
 * So there is no "generate card" button, no spinner-then-reveal, no modal to
 * open. The card fetches itself the instant the game record exists (the server
 * emits `shareReady` with the id) and is simply THERE, under the scores, while
 * the table is still laughing. What you see is exactly the PNG that gets sent —
 * it is the same image file.
 *
 * The controls adapt to where sharing actually happens on the platform:
 *   - phone: the native share sheet, WITH THE FILE, so it lands in WhatsApp or
 *     iMessage as a picture in the conversation rather than a grey link box.
 *   - desktop: the share target is the clipboard. Copy the image (paste into
 *     Slack), or copy the link.
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Btn } from './Btn';
import {
  fetchShareView, shareCard, copyImage, copyLink,
  canShareFiles, canShareLink, canCopyImage,
  type ShareView, type ShareOutcome,
} from '../../services/shareService';
import { trackShareSent } from '../../services/growthService';

interface Props {
  /** Arrives on the `shareReady` socket event, a beat after the game ends. */
  gameId: string | null;
  /** Rendered small, under the card. */
  caption?: string;
}

export function ShareSheet({ gameId, caption }: Props) {
  const [view, setView] = useState<ShareView | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!gameId) return;
    void (async () => {
      const v = await fetchShareView(gameId);
      if (alive) setView(v);
    })();
    return () => { alive = false; };
  }, [gameId]);

  // The card is not ready yet (or the record failed to save). Say nothing —
  // an empty slot is better than a broken promise, and the rest of the results
  // screen is unaffected.
  if (!view) return null;

  const announce = (outcome: ShareOutcome) => {
    trackShareSent(outcome, gameId);
    if (outcome === 'cancelled') return; // not a failure; they changed their mind
    setFlash(
      outcome === 'shared' ? 'Sent.'
      : outcome === 'copied-image' ? 'Image copied — paste it anywhere.'
      : outcome === 'copied-link' ? 'Link copied.'
      : 'Could not share — try copying the link.',
    );
    window.setTimeout(() => setFlash(null), 2600);
  };

  const mobile = canShareFiles() || canShareLink();

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="wr-col gap-2"
      style={{ width: '100%', maxWidth: 460, margin: '0 auto' }}
    >
      {/* THE CARD. This <img> is byte-for-byte the file the share sheet sends. */}
      <div
        style={{
          borderRadius: 16,
          overflow: 'hidden',
          border: '2px solid var(--ink, #16161A)',
          boxShadow: '6px 6px 0 var(--ink, #16161A)',
          background: '#FAF7F2',
          lineHeight: 0,
          // Reserve the 1200:630 box up front so the results screen doesn't
          // lurch when the PNG lands.
          aspectRatio: '1200 / 630',
        }}
      >
        <img
          src={view.image}
          alt={view.shareText}
          width={1200}
          height={630}
          loading="eager"
          onLoad={() => setLoaded(true)}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
            opacity: loaded ? 1 : 0,
            transition: 'opacity .35s ease',
          }}
        />
      </div>

      {caption ? (
        <div className="wr-caption" style={{ textAlign: 'center', color: 'var(--muted)' }}>
          {caption}
        </div>
      ) : null}

      {mobile ? (
        <Btn
          kind="primary"
          size="xl"
          full
          icon="Share2"
          onClick={async () => announce(await shareCard(view))}
        >
          Send this to the group chat
        </Btn>
      ) : (
        <div className="wr-row gap-2">
          {canCopyImage() ? (
            <Btn kind="primary" full icon="Image" onClick={async () => announce(await copyImage(view))}>
              Copy image
            </Btn>
          ) : null}
          <Btn kind="ghost" full icon="Link" onClick={async () => announce(await copyLink(view))}>
            Copy link
          </Btn>
        </div>
      )}

      <div
        aria-live="polite"
        className="wr-caption"
        style={{ minHeight: 18, textAlign: 'center', color: 'var(--muted)' }}
      >
        {flash}
      </div>
    </motion.div>
  );
}
